/*
 * The Bagel Board Co. — theme.js
 *
 * Dark mode toggle logic and system-preference listener.
 *
 * Flash-of-wrong-theme prevention is handled by an INLINE snippet in
 * layout/theme.liquid <head>, which runs synchronously before any CSS
 * is applied and before the body paints. That snippet reads
 * localStorage + prefers-color-scheme and sets data-theme on <html>.
 * The canonical inline snippet source is documented in
 * plans/design-system.md Section 5.2.
 *
 * This file (theme.js) loads deferred and handles:
 *   - Wiring up [data-theme-toggle] buttons in the DOM
 *   - Updating localStorage and data-theme when the toggle fires
 *   - Keeping aria-label on toggle buttons accurate
 *   - Listening for system preference changes when no manual override
 *     is stored
 */

(function () {
  "use strict";

  var STORAGE_KEY = "bagelboard-theme";
  var root = document.documentElement;

  function safeStorageGet(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function safeStorageSet(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      /* storage blocked; toggle works in-session only */
    }
  }

  function currentTheme() {
    return root.getAttribute("data-theme") === "dark" ? "dark" : "light";
  }

  function updateToggleLabels() {
    var current = currentTheme();
    var next = current === "dark" ? "light" : "dark";
    var toggles = document.querySelectorAll("[data-theme-toggle]");
    toggles.forEach(function (toggle) {
      toggle.setAttribute("aria-label", "Switch to " + next + " mode");
    });
  }

  function toggleTheme() {
    var next = currentTheme() === "dark" ? "light" : "dark";
    root.setAttribute("data-theme", next);
    safeStorageSet(STORAGE_KEY, next);
    updateToggleLabels();
  }

  // Wire up any [data-theme-toggle] buttons currently in the DOM
  function wireToggles() {
    var toggles = document.querySelectorAll("[data-theme-toggle]");
    toggles.forEach(function (toggle) {
      if (toggle.dataset.themeToggleWired === "true") return;
      toggle.addEventListener("click", toggleTheme);
      toggle.dataset.themeToggleWired = "true";
    });
    updateToggleLabels();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", wireToggles);
  } else {
    wireToggles();
  }

  // React to system preference changes only if no manual override is stored
  if (window.matchMedia) {
    var mql = window.matchMedia("(prefers-color-scheme: dark)");
    var handler = function (e) {
      if (!safeStorageGet(STORAGE_KEY)) {
        root.setAttribute("data-theme", e.matches ? "dark" : "light");
        updateToggleLabels();
      }
    };
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
    } else if (mql.addListener) {
      // Safari <14 fallback
      mql.addListener(handler);
    }
  }

  // ==========================================================================
  // Primary nav dropdowns (hybrid hover-on-fine-pointer / click otherwise)
  // ==========================================================================

  var FINE_POINTER = window.matchMedia && window.matchMedia("(pointer: fine)").matches;
  var HOVER_OPEN_DELAY = 80;
  var HOVER_CLOSE_DELAY = 140;

  function closeDropdown(button) {
    if (button.getAttribute("aria-expanded") !== "true") return;
    var id = button.getAttribute("aria-controls");
    var menu = id && document.getElementById(id);
    if (menu) menu.setAttribute("hidden", "");
    button.setAttribute("aria-expanded", "false");
  }

  function openDropdown(button) {
    if (button.getAttribute("aria-expanded") === "true") return;
    closeAllDropdowns();
    var id = button.getAttribute("aria-controls");
    var menu = id && document.getElementById(id);
    if (menu) menu.removeAttribute("hidden");
    button.setAttribute("aria-expanded", "true");
  }

  function closeAllDropdowns() {
    document.querySelectorAll('[data-nav-dropdown][aria-expanded="true"]').forEach(closeDropdown);
  }

  function wireDropdowns() {
    var triggers = document.querySelectorAll("[data-nav-dropdown]");
    triggers.forEach(function (button) {
      if (button.dataset.dropdownWired === "true") return;
      button.dataset.dropdownWired = "true";

      var openTimer = null;
      var closeTimer = null;
      var item = button.parentElement;

      // Click / Enter / Space — works for keyboard, touch, and mouse
      button.addEventListener("click", function (event) {
        event.preventDefault();
        if (button.getAttribute("aria-expanded") === "true") {
          closeDropdown(button);
        } else {
          openDropdown(button);
          // Focus the first submenu link when opened via keyboard
          var menuId = button.getAttribute("aria-controls");
          var menu = menuId && document.getElementById(menuId);
          var first = menu && menu.querySelector("a, button");
          if (first && event.detail === 0) first.focus();
        }
      });

      // Hover open/close — only on devices with fine pointer
      if (FINE_POINTER && item) {
        item.addEventListener("pointerenter", function (e) {
          if (e.pointerType !== "mouse") return;
          clearTimeout(closeTimer);
          openTimer = setTimeout(function () { openDropdown(button); }, HOVER_OPEN_DELAY);
        });
        item.addEventListener("pointerleave", function (e) {
          if (e.pointerType !== "mouse") return;
          clearTimeout(openTimer);
          closeTimer = setTimeout(function () { closeDropdown(button); }, HOVER_CLOSE_DELAY);
        });
      }
    });

    // Escape closes any open dropdown and returns focus to its trigger
    document.addEventListener("keydown", function (event) {
      if (event.key !== "Escape") return;
      var open = document.querySelector('[data-nav-dropdown][aria-expanded="true"]');
      if (open) {
        closeDropdown(open);
        open.focus();
      }
    });

    // Click outside closes any open dropdown
    document.addEventListener("click", function (event) {
      if (event.target.closest("[data-nav-dropdown]")) return;
      if (event.target.closest(".site-nav__submenu")) return;
      closeAllDropdowns();
    });
  }

  // ==========================================================================
  // Mobile overlay menu (open / close / focus trap / body scroll lock)
  // ==========================================================================

  var FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])"
  ].join(",");

  var lastFocused = null;

  function openMobileMenu() {
    var overlay = document.querySelector("[data-mobile-nav]");
    var toggle = document.querySelector("[data-menu-toggle]");
    if (!overlay || !toggle) return;

    lastFocused = document.activeElement;
    overlay.removeAttribute("hidden");
    toggle.setAttribute("aria-expanded", "true");
    toggle.setAttribute("aria-label", "Close menu");
    document.documentElement.setAttribute("data-menu-open", "true");

    // Focus the first focusable element in the overlay
    var first = overlay.querySelector(FOCUSABLE_SELECTOR);
    if (first) first.focus();
  }

  function closeMobileMenu() {
    var overlay = document.querySelector("[data-mobile-nav]");
    var toggle = document.querySelector("[data-menu-toggle]");
    if (!overlay) return;

    overlay.setAttribute("hidden", "");
    if (toggle) {
      toggle.setAttribute("aria-expanded", "false");
      toggle.setAttribute("aria-label", "Open menu");
    }
    document.documentElement.removeAttribute("data-menu-open");
    if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
  }

  function trapFocus(event) {
    if (event.key !== "Tab") return;
    if (document.documentElement.getAttribute("data-menu-open") !== "true") return;

    var overlay = document.querySelector("[data-mobile-nav]");
    if (!overlay) return;

    var focusables = Array.prototype.slice.call(overlay.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(function (el) { return !el.hasAttribute("hidden") && el.offsetParent !== null; });
    if (focusables.length === 0) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function wireMobileMenu() {
    var toggle = document.querySelector("[data-menu-toggle]");
    var close = document.querySelector("[data-menu-close]");

    if (toggle && toggle.dataset.menuWired !== "true") {
      toggle.addEventListener("click", openMobileMenu);
      toggle.dataset.menuWired = "true";
    }
    if (close && close.dataset.menuWired !== "true") {
      close.addEventListener("click", closeMobileMenu);
      close.dataset.menuWired = "true";
    }

    // Escape closes overlay
    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && document.documentElement.getAttribute("data-menu-open") === "true") {
        closeMobileMenu();
      }
    });

    document.addEventListener("keydown", trapFocus);
  }

  // ==========================================================================
  // Sticky header — scroll-away on down, reveal on up
  // ==========================================================================

  function wireScrollDirection() {
    var lastY = window.scrollY;
    var threshold = 80;       // don't slide away until scrolled past promo bar
    var minDelta = 8;         // ignore micro-jitters
    var rafScheduled = false;

    function update() {
      rafScheduled = false;
      var currentY = window.scrollY;
      var delta = currentY - lastY;

      if (Math.abs(delta) < minDelta) return;

      if (currentY < threshold) {
        document.documentElement.removeAttribute("data-scroll-direction");
      } else if (delta > 0) {
        document.documentElement.setAttribute("data-scroll-direction", "down");
      } else {
        document.documentElement.setAttribute("data-scroll-direction", "up");
      }

      lastY = currentY;
    }

    window.addEventListener("scroll", function () {
      if (rafScheduled) return;
      rafScheduled = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
  }

  // ==========================================================================
  // Cart drawer (slide-in from right)
  //
  // Triggered by:
  //   - clicks on [data-cart-toggle] (header cart icon)
  //   - product form Add to Cart submissions (wired in feat/product-bagel-board)
  //
  // Closed by:
  //   - clicks on [data-cart-close] (close button, backdrop, "Browse" CTA in
  //     empty state, "View full cart" link)
  //   - Escape key
  //
  // Cart mutations use Shopify's Cart Sections API. POST /cart/change.js with
  // body { sections: 'cart-drawer,main-cart' } returns the re-rendered section
  // HTML keyed by section name. replaceCartSections() walks the response and
  // replaces every [data-cart-content] in the DOM whose [data-cart-section]
  // ancestor matches a key in the response. The drawer (always rendered via
  // {% section 'cart-drawer' %} in layout/theme.liquid) hot-updates from any
  // surface; the standalone /cart page (data-cart-section="main-cart") also
  // hot-updates when present.
  // ==========================================================================

  var cartLastFocused = null;

  function getCartDrawer() {
    return document.querySelector("[data-cart-drawer]");
  }

  function openCart() {
    var drawer = getCartDrawer();
    if (!drawer) return;

    cartLastFocused = document.activeElement;
    drawer.removeAttribute("hidden");
    // Force reflow so the transition runs from translateX(100%) -> 0
    void drawer.offsetWidth;
    drawer.setAttribute("data-open", "true");
    document.documentElement.setAttribute("data-cart-open", "true");

    var closeBtn = drawer.querySelector(".cart-drawer__close");
    if (closeBtn) closeBtn.focus();
  }

  function closeCart() {
    var drawer = getCartDrawer();
    if (!drawer) return;

    drawer.removeAttribute("data-open");
    document.documentElement.removeAttribute("data-cart-open");

    // After the slide-out transition, hide the drawer entirely
    var reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setTimeout(function () { drawer.setAttribute("hidden", ""); }, reduced ? 0 : 200);

    if (cartLastFocused && typeof cartLastFocused.focus === "function") {
      cartLastFocused.focus();
    }
  }

  function isCartOpen() {
    return document.documentElement.getAttribute("data-cart-open") === "true";
  }

  function updateCartCount(count) {
    var label = "Cart, " + count + " " + (count === 1 ? "item" : "items");
    document.querySelectorAll("[data-cart-toggle]").forEach(function (link) {
      link.setAttribute("aria-label", label);
      var badge = link.querySelector(".site-header__cart-count");
      if (count > 0) {
        if (!badge) {
          badge = document.createElement("span");
          badge.className = "site-header__cart-count";
          badge.setAttribute("aria-hidden", "true");
          link.appendChild(badge);
        }
        badge.textContent = String(count);
      } else if (badge) {
        badge.remove();
      }
    });
  }

  function replaceCartSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (sectionName) {
      var html = sections[sectionName];
      if (!html) return;
      var tmp = document.createElement("div");
      tmp.innerHTML = html;
      var newContent = tmp.querySelector("[data-cart-content]");
      if (!newContent) return;
      var existing = document.querySelector(
        '[data-cart-section="' + sectionName + '"] [data-cart-content]'
      );
      if (existing) existing.replaceWith(newContent);
    });
  }

  // Public API: callable from product forms and elsewhere.
  // Exposed on window.bagelboardCart so feat/product-bagel-board can call
  // addToCart() from the Add to Cart submit handler.
  var cartApi = {
    open: openCart,
    close: closeCart,

    addToCart: function (formData) {
      // Request drawer + main-cart so both hot-update if present
      formData.append("sections", "cart-drawer,main-cart");
      return fetch("/cart/add.js", {
        method: "POST",
        headers: { "Accept": "application/json" },
        body: formData
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (err) {
              throw new Error(err.description || ("Add to cart failed: " + res.status));
            });
          }
          return res.json();
        })
        .then(function (data) {
          replaceCartSections(data.sections);
          // /cart/add.js returns the line, not the full count — fetch it
          return fetch("/cart.js").then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          updateCartCount(cart.item_count);
          openCart();
          return cart;
        });
    },

    changeLineQty: function (lineKey, newQty) {
      return fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          id: lineKey,
          quantity: Math.max(0, parseInt(newQty, 10) || 0),
          sections: "cart-drawer,main-cart"
        })
      })
        .then(function (res) {
          if (!res.ok) throw new Error("Cart change failed: " + res.status);
          return res.json();
        })
        .then(function (data) {
          replaceCartSections(data.sections);
          updateCartCount(data.item_count);
          return data;
        });
    }
  };

  window.bagelboardCart = cartApi;

  function trapCartFocus(event) {
    if (event.key !== "Tab") return;
    if (!isCartOpen()) return;

    var drawer = getCartDrawer();
    if (!drawer) return;

    var focusables = Array.prototype.slice.call(drawer.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(function (el) { return !el.hasAttribute("hidden") && el.offsetParent !== null; });
    if (focusables.length === 0) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function wireCartDrawer() {
    // Header cart-icon and any other [data-cart-toggle] click -> open drawer
    document.addEventListener("click", function (event) {
      var toggle = event.target.closest("[data-cart-toggle]");
      if (!toggle) return;
      event.preventDefault();
      openCart();
    });

    // Backdrop, close button, and "View full cart" -> close drawer
    document.addEventListener("click", function (event) {
      var closeEl = event.target.closest("[data-cart-close]");
      if (!closeEl) return;
      // Don't preventDefault on links — "View full cart" should still navigate
      var isLink = closeEl.tagName === "A" && closeEl.getAttribute("href");
      closeCart();
      if (!isLink) {
        // Buttons stay where they are; links navigate naturally
      }
    });

    // Escape closes drawer
    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && isCartOpen()) closeCart();
    });

    // Quantity stepper +/- and Remove button
    document.addEventListener("click", function (event) {
      var line = event.target.closest("[data-line-key]");
      if (!line) return;
      var lineKey = line.dataset.lineKey;
      var qtyInput = line.querySelector("[data-cart-qty]");
      var currentQty = parseInt(qtyInput ? qtyInput.value : "0", 10) || 0;

      if (event.target.closest("[data-cart-qty-decrease]")) {
        cartApi.changeLineQty(lineKey, Math.max(0, currentQty - 1));
      } else if (event.target.closest("[data-cart-qty-increase]")) {
        cartApi.changeLineQty(lineKey, currentQty + 1);
      } else if (event.target.closest("[data-cart-remove]")) {
        cartApi.changeLineQty(lineKey, 0);
      }
    });

    // Direct quantity input change (typing, paste)
    document.addEventListener("change", function (event) {
      if (!event.target.matches("[data-cart-qty]")) return;
      var line = event.target.closest("[data-line-key]");
      if (!line) return;
      var newQty = Math.max(0, parseInt(event.target.value, 10) || 0);
      cartApi.changeLineQty(line.dataset.lineKey, newQty);
    });

    // Focus trap inside the drawer when open
    document.addEventListener("keydown", trapCartFocus);
  }

  // ==========================================================================
  // Product page — gallery, variant picker, Add to Cart, sticky bar
  // ==========================================================================

  function wireProductGallery() {
    var gallery = document.querySelector("[data-product-gallery]");
    if (!gallery) return;

    var mainImg = gallery.querySelector("[data-gallery-main]");
    var thumbs = gallery.querySelectorAll("[data-gallery-thumb]");

    // Thumbnail click -> swap main image
    thumbs.forEach(function (thumb) {
      thumb.addEventListener("click", function () {
        var src = thumb.dataset.fullSrc;
        var alt = thumb.dataset.fullAlt;
        if (mainImg && src) {
          mainImg.src = src;
          mainImg.alt = alt || "";
        }
        thumbs.forEach(function (t) {
          t.classList.remove("product-gallery__thumb-btn--active");
          t.removeAttribute("aria-current");
        });
        thumb.classList.add("product-gallery__thumb-btn--active");
        thumb.setAttribute("aria-current", "true");
      });
    });
  }

  var galleryLastFocused = null;

  function openLightbox() {
    var lightbox = document.querySelector("[data-gallery-lightbox]");
    var mainImg = document.querySelector("[data-gallery-main]");
    var lbImg = document.querySelector("[data-gallery-lightbox-img]");
    if (!lightbox || !mainImg || !lbImg) return;

    galleryLastFocused = document.activeElement;
    lbImg.src = mainImg.src;
    lbImg.alt = mainImg.alt || "";
    lightbox.removeAttribute("hidden");
    document.documentElement.setAttribute("data-lightbox-open", "true");

    var closeBtn = lightbox.querySelector(".product-lightbox__close");
    if (closeBtn) closeBtn.focus();
  }

  function closeLightbox() {
    var lightbox = document.querySelector("[data-gallery-lightbox]");
    if (!lightbox) return;
    lightbox.setAttribute("hidden", "");
    document.documentElement.removeAttribute("data-lightbox-open");
    if (galleryLastFocused && typeof galleryLastFocused.focus === "function") {
      galleryLastFocused.focus();
    }
  }

  function wireLightbox() {
    document.addEventListener("click", function (event) {
      if (event.target.closest("[data-gallery-open]")) {
        event.preventDefault();
        openLightbox();
      } else if (event.target.closest("[data-gallery-close]")) {
        closeLightbox();
      }
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && document.documentElement.getAttribute("data-lightbox-open") === "true") {
        closeLightbox();
      }
    });
  }

  // ----- Variant button picker -----

  function wireVariantPicker() {
    var form = document.querySelector("[data-product-form]");
    if (!form) return;

    var variants;
    try {
      variants = JSON.parse(form.dataset.variants || "[]");
    } catch (e) {
      console.error("Variant data parse failed:", e);
      return;
    }
    if (!variants || variants.length === 0) return;

    var optionButtons = form.querySelectorAll("[data-variant-option]");
    var hiddenId = form.querySelector("[data-variant-id]");
    var priceEl = form.querySelector("[data-variant-price]");
    var comparePriceEl = form.querySelector("[data-variant-compare-price]");
    var submitBtn = form.querySelector("[data-add-to-cart]");
    var submitLabel = form.querySelector("[data-add-to-cart-label]");
    var stickyBtn = document.querySelector("[data-sticky-add-to-cart]");
    var stickyPrice = document.querySelector("[data-sticky-price]");
    // Stand-only hooks (Step 3). Null on Boards / Kits — every dependent
    // operation below guards on truthiness before using these.
    var standSummaryEl = document.querySelector("[data-stand-summary]");
    var capacityRows = document.querySelectorAll("[data-product-capacity-table] [data-rod-count]");

    function getCurrentSelection() {
      // Read selected option per option position
      var selected = {};
      var groups = form.querySelectorAll(".variant-options");
      groups.forEach(function (group) {
        var pos = parseInt(group.querySelector("[data-option-position]").dataset.optionPosition, 10);
        var sel = group.querySelector(".variant-btn--selected");
        if (sel) selected[pos] = sel.dataset.optionValue;
      });
      return selected;
    }

    function findVariant(selection) {
      return variants.find(function (v) {
        // Variant.options is an array indexed by position-1
        var match = true;
        Object.keys(selection).forEach(function (pos) {
          var idx = parseInt(pos, 10) - 1;
          if (v.options[idx] !== selection[pos]) match = false;
        });
        return match;
      });
    }

    function formatPrice(cents) {
      // Naive money formatter — assumes USD. Shopify's Liquid `money` filter
      // is the canonical truth on initial render; this matches the same shape
      // (e.g. "$44.85") for visual consistency on variant swap.
      return "$" + (cents / 100).toFixed(2);
    }

    function composeStandSummary(rodCount, schmearCount) {
      // Mirrors the Liquid first-paint composition in sections/main-product.liquid.
      // Assumes admin option order: option1 = Rod count, option2 = Schmear kits.
      var base = rodCount + "-rod stand";
      if (!schmearCount || schmearCount === "None") return base;
      if (schmearCount === "1") return base + " with 1 schmear kit";
      return base + " with " + schmearCount + " schmear kits";
    }

    function highlightCapacityRow(rodCount) {
      if (!capacityRows || capacityRows.length === 0) return;
      capacityRows.forEach(function (row) {
        var matches = row.dataset.rodCount === String(rodCount);
        row.classList.toggle("product-capacity-table__row--selected", matches);
        if (matches) {
          row.setAttribute("aria-current", "true");
        } else {
          row.removeAttribute("aria-current");
        }
      });
    }

    function applyVariant(variant) {
      if (!variant) return;
      hiddenId.value = variant.id;

      // Update URL ?variant= for bookmarking / sharing without reload
      var url = new URL(window.location.href);
      url.searchParams.set("variant", String(variant.id));
      window.history.replaceState({}, "", url.toString());

      // Price
      if (priceEl) priceEl.textContent = formatPrice(variant.price);
      if (stickyPrice) stickyPrice.textContent = formatPrice(variant.price);
      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.innerHTML = "<s>" + formatPrice(variant.compare_at_price) + "</s>";
          comparePriceEl.hidden = false;
        } else {
          comparePriceEl.hidden = true;
        }
      }

      // Submit button availability
      if (submitBtn) {
        submitBtn.disabled = !variant.available;
        submitBtn.setAttribute("aria-disabled", !variant.available);
        if (submitLabel) submitLabel.textContent = variant.available ? "Add to cart" : "Sold out";
      }
      if (stickyBtn) {
        stickyBtn.disabled = !variant.available;
        stickyBtn.textContent = variant.available ? "Add to cart" : "Sold out";
      }

      // Stand-only enhancements (no-op on other product templates).
      if (standSummaryEl) {
        standSummaryEl.textContent = composeStandSummary(variant.options[0], variant.options[1]);
      }
      highlightCapacityRow(variant.options[0]);
    }

    // First-paint capacity-row highlight matches the variant rendered server-side.
    if (capacityRows && capacityRows.length > 0) {
      var initialVariant = variants.find(function (v) {
        return String(v.id) === hiddenId.value;
      }) || variants[0];
      if (initialVariant) highlightCapacityRow(initialVariant.options[0]);
    }

    optionButtons.forEach(function (btn) {
      btn.addEventListener("click", function () {
        var pos = btn.dataset.optionPosition;
        // Deselect siblings in same option
        var siblings = btn.parentElement.querySelectorAll("[data-variant-option]");
        siblings.forEach(function (s) {
          s.classList.remove("variant-btn--selected");
          s.setAttribute("aria-checked", "false");
        });
        btn.classList.add("variant-btn--selected");
        btn.setAttribute("aria-checked", "true");

        var selection = getCurrentSelection();
        var variant = findVariant(selection);
        applyVariant(variant);
      });
    });
  }

  // ----- Add to Cart (intercept form submit, route through cart drawer API) -----

  function wireAddToCart() {
    var form = document.querySelector("[data-product-form]");
    var stickyBtn = document.querySelector("[data-sticky-add-to-cart]");
    if (!form) return;

    function submitToCart() {
      if (!window.bagelboardCart) return; // Fall back to native form submit
      var btn = form.querySelector("[data-add-to-cart]");
      if (btn && btn.disabled) return;

      // Disable buttons while in flight
      if (btn) btn.disabled = true;
      if (stickyBtn) stickyBtn.disabled = true;

      var formData = new FormData(form);
      window.bagelboardCart.addToCart(formData)
        .catch(function (err) {
          console.error("Add to cart failed:", err);
          alert(err.message || "Could not add to cart. Try again.");
        })
        .finally(function () {
          if (btn) btn.disabled = false;
          if (stickyBtn) stickyBtn.disabled = false;
        });
    }

    form.addEventListener("submit", function (event) {
      if (!window.bagelboardCart) return; // Native submit
      event.preventDefault();
      submitToCart();
    });

    if (stickyBtn) {
      stickyBtn.addEventListener("click", function (event) {
        event.preventDefault();
        submitToCart();
      });
    }
  }

  // ----- Quantity stepper -----

  function wireQuantityStepper() {
    var form = document.querySelector("[data-product-form]");
    if (!form) return;

    var input = form.querySelector("[data-qty-input]");
    var dec = form.querySelector("[data-qty-decrease]");
    var inc = form.querySelector("[data-qty-increase]");
    if (!input) return;

    if (dec) dec.addEventListener("click", function () {
      var current = parseInt(input.value, 10) || 1;
      input.value = Math.max(1, current - 1);
    });
    if (inc) inc.addEventListener("click", function () {
      var current = parseInt(input.value, 10) || 1;
      input.value = current + 1;
    });
  }

  // ----- Sticky mobile Add-to-Cart bar reveal -----

  function wireStickyBar() {
    var bar = document.querySelector("[data-product-sticky-bar]");
    var form = document.querySelector("[data-product-form]:not(.product-form--compact)");
    if (!bar || !form) return;

    // Reveal the sticky bar once the inline form scrolls out of view
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          bar.removeAttribute("data-visible");
        } else {
          bar.setAttribute("data-visible", "true");
        }
      });
    }, { rootMargin: "0px 0px -100px 0px" });

    observer.observe(form);
  }

  function bootProductPage() {
    if (!document.querySelector("[data-product-form]")) return; // Not on a product page
    wireProductGallery();
    wireLightbox();
    wireVariantPicker();
    wireQuantityStepper();
    wireAddToCart();
    wireStickyBar();
  }

  // ==========================================================================
  // Boot all header behaviors after DOM is ready
  // ==========================================================================

  function bootHeader() {
    wireDropdowns();
    wireMobileMenu();
    wireScrollDirection();
    wireCartDrawer();
    bootProductPage();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootHeader);
  } else {
    bootHeader();
  }
})();
